/**
 * aiChatInitV2 · AI 分类首页推荐卡接口（POST /web/workgroup/folder/ai/v2/chat/init）
 *
 * 2026-07-03 新接口，专门返回首页推荐卡：服务端直接预拼「单/单/单/多」循环的
 * recommend_cards 序列（每组 3 单级 + 1 多级），前端不组卡、不判断顺序，
 * 只按 4 张一组取用 +「换一批」向后翻组、翻完循环（见 utils/recommendCards.ts）。
 *
 * 与团队预热接口 /ai/chat/init（aiChatInit.ts）完全解耦：那边继续返回
 * aggregated / note / recommended_prompts（QuickPrompts 仍在消费、本次无改动），
 * 本接口只服务 RecommendCards 推荐卡片。
 *
 * 响应为上游原始 JSON（无 {code,msg,data} 外层包装——接口文档「通用约定」标注的
 * v2 init 例外），仅一个字段 recommend_cards。
 *
 * 接口文档: 钉钉 QG53mjyd80R04wE4HwylnLN3V6zbX04v §三
 */

declare const __API_SERVER_HOST__: string

/**
 * 单级卡 · 后端原始形态
 *
 * dimension ∈ time / member / watermark / customer_visit（拜访客户，仅拜访团队）。
 * values 服务端已做过滤（频次倒序 → 超 15 字丢弃 → 去重 → 取前 5），前端直接渲染。
 */
export interface RawRecommendCardSingle {
  type: 'single'
  /** 分类维度语义标识 · 同时用作 icon 映射的兜底依据 */
  dimension: string
  /** 卡片标题，点击后也作为 query 原文发给 /send */
  text: string
  /** icon 语义类型 · 前端经 iconTypeToAntdIcon 映射 */
  icon_type: string
  /** 卡片副标题（说明 AI 提取来源，如「提取拍摄时间」）· 当前推荐卡 UI 未消费 */
  subtitle: string
  /** 维值标签 · 至多 5 个 */
  values: string[]
}

/** 多级卡的单层维值（level1 取 1 个维值、level2 至多 3 个） */
export interface RawRecommendCardLevel {
  /** 该层的分类维度语义标识 */
  dimension: string
  /** 该层维度的展示名（如「拍摄月份」「项目名称」） */
  name: string
  /** 该层选中的维值 */
  values: string[]
}

/** 多级卡 · 后端原始形态（每组第 4 张） */
export interface RawRecommendCardMulti {
  type: 'multi'
  /** 卡片标题，点击后也作为 query 原文发给 /send */
  text: string
  level1: RawRecommendCardLevel
  level2: RawRecommendCardLevel
}

export type RawRecommendCard = RawRecommendCardSingle | RawRecommendCardMulti

export interface AiChatInitV2Response {
  /** 推荐卡序列 · 服务端按「单/单/单/多」循环预拼 */
  recommend_cards: RawRecommendCard[]
}

export interface AiChatInitV2Options {
  /** 团队 UUID（group_id_real）· 必填 */
  groupID: string
  signal?: AbortSignal
}

/**
 * 调一次 /web/workgroup/folder/ai/v2/chat/init · 拉取首页推荐卡序列
 *
 * 失败（网络错 / 401 鉴权 / 400 参数 / 502 上游不可用）时抛 Error；
 * 调用方（loadRecommendCards）降级兜底：渲染层走 FALLBACK_RECOMMEND_CARDS，
 * 不影响用户发消息（/send 与本接口解耦）。
 */
export async function fetchAiChatInitV2(
  options: AiChatInitV2Options
): Promise<AiChatInitV2Response> {
  const url = __API_SERVER_HOST__ + '/web/workgroup/folder/ai/v2/chat/init'
  // ⚠️ 请求体是下划线 group_id（接口文档 §三），区别于接口一的驼峰 groupID
  const body: Record<string, string> = { group_id: options.groupID }

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  return res.json()
}
